import type { ArchitecturalColumnDescriptor } from "building-types/columns/columnDescriptorTypes";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { Porch } from "building-types/porches/porch";
import { ColonnadePointType } from "building-types/porches/porchColonnadePoint";
import type {
  ExteriorMaterialOverrideWithKeyMeasures,
  PorchKeyMeasuresGeometryType,
} from "building-types/porches/porchKeyMeasuresTypes";
import type { ColumnCatalogSettings } from "building-types/settings/columnCatalogSettings";
import type { PlanSettings } from "building-types/settings/planSettings";
import type { PorchSettings } from "building-types/settings/porchSettings";
import type { PorchPlanSettings, PorchSettingsBase } from "building-types/settings/porchSettingsTypes";
import type { ViewEntityDescriptor } from "building-types/views/viewEntityTypes";
import { arrayDataToObjectArrayData, edgeDataRemap } from "doc/provenance/edgeDataTools";
import { arrayUnionDistinct } from "doc/provenance/halfEdgeDataTools";
import { getArchitecturalColumnOrDefault, getColumnTable } from "doc/utils/columnDescriptorUtils";
import {
  polyLine2ByTwoPoints,
  polyLine2Copy,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2MergeCoincidentVerticesExtended,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { ascendingSortCompare } from "geom/utils";
import type { UVTransformation } from "geom/uv";
import { UVGeneration } from "geom/uv";
import { vec2Lerp } from "geom/vec2";

export interface PorchColonnadeSettings {
  readonly topDatum: number;
  readonly bottomDatum: number;
}

export type PorchSettingsColumnPlacement = Pick<
  PorchSettings,
  | "columnLateralOffset"
  | "maxColumnSpacing"
  | "minColumnSpacing"
  | "doorPathColumnSpread"
  | "doorPathColumnSpreadMode"
  | "colonnadeOverrideValues"
  | "frontColonnadeColumnOverride"
>;

export type PorchColumnSettings = Pick<
  PorchSettings,
  | "columnId"
  | "internalColumnStructureSize"
  | "multiColumnSpread"
  | "multiColumn"
  | "multiColumnFrontOnly"
  | "headerDepth"
  | "columnCasingOffset"
>;

export type ResolvedPorchSettingsAndDefaults = PorchColumnSettings &
  PorchColonnadeSettings &
  PorchSettingsColumnPlacement;

export const BRACE_DIST = 18;
export const BRACE_WIDTH = 3.5;

export function exteriorMaterialOverrideWithKeyMeasuresNew(
  exteriorMaterial: ExteriorMaterial | undefined,
  uvGen: UVGeneration = UVGeneration.NONE,
  porchKeyMeasuresGeometryType: PorchKeyMeasuresGeometryType,
  viewEntityDescriptor?: ViewEntityDescriptor,
  materialTransformation?: UVTransformation,
  columnIndex?: number,
): ExteriorMaterialOverrideWithKeyMeasures {
  return {
    exteriorMaterial,
    uvGen,
    materialTransformation,
    porchKeyMeasuresGeometryType,
    viewEntityDescriptor,
    columnIndex,
  };
}

// create array of PolyLine2 from splitting PolyLine2 at t[];
export function shatterLine2(pl: PolyLine2, tSplit: number[]): PolyLine2[] {
  const line = polyLine2Copy(pl);
  const subLines: PolyLine2[] = [];

  for (let i = 0; i < tSplit.length - 1; i += 1) {
    const [startX, startY] = vec2Lerp(line[0], line[1], line[2], line[3], tSplit[i]);
    const [endX, endY] = vec2Lerp(line[0], line[1], line[2], line[3], tSplit[i + 1]);
    const subLine = polyLine2ByTwoPoints(startX, startY, endX, endY);

    subLines.push(subLine);
  }
  return subLines;
}

// break a PolyLine2 at its vertices
export function shatterPolyLine2(pl: PolyLine2): PolyLine2[] {
  const segCount = polyLine2EdgeCount(pl);
  const subLines: PolyLine2[] = [];

  for (let i = 0; i < segCount; i += 1) {
    const subLine = polyLine2Edge(pl, i);
    subLines.push(subLine);
  }

  return subLines;
}

export function splitIntervalAtParams(intervalStart: number, intervalEnd: number, gapParams: number[]): number[] {
  if (!(intervalEnd - intervalStart > 0)) {
    console.assert(false, "cannot cut gaps into zero length interval");
  }
  gapParams.sort(ascendingSortCompare);

  const resIntervals: number[] = [];
  let curStart = intervalStart;

  for (const gapParam of gapParams) {
    if (gapParam <= curStart || gapParam >= intervalEnd) {
      continue;
    }
    resIntervals.push(curStart);
    resIntervals.push(gapParam);
    curStart = gapParam;
  }

  resIntervals.push(curStart);
  resIntervals.push(intervalEnd);

  return resIntervals;
}

interface PorchInfo {
  readonly colonnadePolyLines: PolyLine2[];
  readonly colonnadeEdgeDataArr: (number | undefined)[][][];
  readonly colonnadeInternalColumnStructureSizes: number[];
}

export function extractPorchAnnotationInfo(porches: Porch[], levelId: string): PorchInfo {
  const colonnadePolyLines: PolyLine2[] = [];
  const colonnadeEdgeDataArr: (number | undefined)[][][] = [];
  const colonnadeInternalColumnStructureSizes: number[] = [];

  for (const porch of porches) {
    if (
      porch.colonnadePoints.filter(
        (x) => x.type === ColonnadePointType.COLUMN || x.type === ColonnadePointType.MULTI_COLUMN_DIM,
      ).length === 0 ||
      porch.rooms[0].level.id !== levelId
    ) {
      continue;
    }

    const colonnade: number[] = [];
    const colonnadeEdgeData: (number | undefined)[][] = [];
    let lastPtType: ColonnadePointType | undefined = undefined;
    let lastPtIndex: number = 0;
    // A degenerate colonnade is where there's two terminus points next to each other in the polyline, and their edges are not connected
    // We don't want to generate an angular dimension between the two terminus points that shouldn't be there
    let degenerateColonnade: boolean = false;
    // do not dimension each multicolumn, use the multicolumn centerline
    for (let i = 0; i < porch.colonnadePoints.length; i++) {
      const pt = porch.colonnadePoints[i];
      if (pt.type !== ColonnadePointType.MULTI_COLUMN && pt.type !== ColonnadePointType.COLUMN) {
        const { x, y, halfEdges } = pt;
        if (pt.type === ColonnadePointType.TERMINUS && lastPtType === ColonnadePointType.TERMINUS) {
          if (i - lastPtIndex > 1) {
            degenerateColonnade = true;
            break;
          }
        }
        colonnade.push(x, y);
        if (i < porch.colonnadePoints.length - 1) {
          colonnadeEdgeData.push(halfEdges);
        }
        lastPtType = pt.type;
        lastPtIndex = i;
      }
    }
    if (degenerateColonnade) {
      continue;
    }

    const [pl, remap] = polyLine2MergeCoincidentVerticesExtended(polyLine2New(colonnade));
    const edgeData = edgeDataRemap(
      arrayDataToObjectArrayData(remap),
      colonnadeEdgeData,
      arrayUnionDistinct,
      polyLine2EdgeCount(pl),
      undefined,
    );

    colonnadePolyLines.push(pl);
    colonnadeEdgeDataArr.push(edgeData);
    colonnadeInternalColumnStructureSizes.push(porch.porchSettings.internalColumnStructureSize);
  }

  return { colonnadePolyLines, colonnadeEdgeDataArr, colonnadeInternalColumnStructureSizes };
}

export function getColumnFromSettings(
  porchSettings: Pick<{ columnId?: string }, "columnId"> | undefined,
  columnSettings: ColumnCatalogSettings,
): ArchitecturalColumnDescriptor {
  const columnTable = getColumnTable(columnSettings);
  const columnId = porchSettings?.columnId;
  return getArchitecturalColumnOrDefault(columnTable, columnId);
}

export function resolvePorchPlanSettings(planSettings: PlanSettings, porchSettings: PorchSettingsBase) {
  return {
    ...planSettings,
    ...porchSettings.porchPlanSettings,
  } as PorchPlanSettings;
}
